import React, { useContext, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { EnvironmentContext } from '../../../providers/environment/EnvironmentContext'
import { guslStorage } from '../../../providers/session/GuslStorage'
import { isDefined } from '../../../utils/TypeCheckers'
import { getStyle } from '../../../utils/CssUtils'
import { PanelConfigDTO } from '../../types'
import { ActionIconStyled } from '../gusl-table/styled/table-controls/styled'
import { initHeaderPanelHiddenFields, toggleHeaderPanelSettings } from '../gusl-table/guslTableSlice'
import { HeaderPanel } from './HeaderPanel'
import { HeaderPanelSettings } from './HeaderPanelSettings'
import { HeaderPanelSettingsPopoverStyled, HeaderPanelsStyled, HeaderPanelStyled } from './styled'

const PANEL_WIDTH = 260

type HeaderPanelsProperties = {
    parentData: any
    panels: PanelConfigDTO[]
    code?: string
}

export const HeaderPanels = ({ parentData, panels, code }: HeaderPanelsProperties): React.ReactElement => {
    const dispatch = useAppDispatch()
    const environmentContext = useContext(EnvironmentContext)
    const isMobile = environmentContext.isMobileDevice()
    const gearIconRef = useRef<HTMLSpanElement>(null)
    const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null)
    const initDoneRef = useRef<boolean>(false)

    const showHeaderPanelSettings: boolean = useAppSelector((state) =>
        isDefined(code) ? (state.guslTableSlice[code!]?.showHeaderPanelSettings ?? false) : false
    )
    const hiddenHeaderPanelFields: string[] = useAppSelector((state) =>
        isDefined(code) ? (state.guslTableSlice[code!]?.hiddenHeaderPanelFields ?? []) : []
    )

    const hasFilterableFields: boolean = panels.some((panel) =>
        (panel.rows || []).some((row) => {
            const col = row.columns[0]
            return (col?.fields || []).some((fld) => fld.filterable)
        })
    )

    const onSettingsClicked = () => {
        if (isDefined(code)) {
            if (!showHeaderPanelSettings && gearIconRef.current) {
                const rect = gearIconRef.current.getBoundingClientRect()
                const spaceOnRight = window.innerWidth - rect.right
                const left = spaceOnRight >= PANEL_WIDTH ? rect.right + 4 : rect.left - PANEL_WIDTH - 4
                setPopoverPos({ top: rect.top, left })
            }
            dispatch(toggleHeaderPanelSettings({ code: code! }))
        }
    }

    const onSettingsClose = () => {
        if (isDefined(code)) {
            dispatch(toggleHeaderPanelSettings({ code: code! }))
            setPopoverPos(null)
        }
    }

    if (!initDoneRef.current && isDefined(code) && panels?.length > 0) {
        initDoneRef.current = true
        const storedVisibility = guslStorage.getHeaderPanelVisibility(code!)
        dispatch(initHeaderPanelHiddenFields({ code: code!, panels, storedVisibility }))
    }

    const renderPanel = (panel: PanelConfigDTO, idx: number): React.ReactElement => {
        const style = getStyle(panel.panelCss, '')
        const key = `panel_${idx}_${parentData?.id || ''}`
        return (
            <HeaderPanelStyled key={key} style={style} id={key}>
                <HeaderPanel parentData={parentData} panel={panel} panelIndex={idx} hiddenFields={hiddenHeaderPanelFields} />
            </HeaderPanelStyled>
        )
    }

    return (
        <div style={{ position: 'relative', display: 'flex', flex: 1, alignItems: 'flex-start' }}>
            <HeaderPanelsStyled id={'header_panels'} style={{ flex: 1 }}>
                {panels?.map((panel, index) => renderPanel(panel, index))}
            </HeaderPanelsStyled>
            {isDefined(code) && hasFilterableFields && (
                <ActionIconStyled ref={gearIconRef} active={showHeaderPanelSettings} isMobile={isMobile} onClick={onSettingsClicked}>
                    <i className={'fa-solid fa-sliders'} />
                </ActionIconStyled>
            )}
            {isDefined(code) && showHeaderPanelSettings && isMobile && (
                <HeaderPanelSettings code={code!} panels={panels} onClose={onSettingsClose} isMobile={true} />
            )}
            {isDefined(code) && showHeaderPanelSettings && !isMobile && popoverPos &&
                ReactDOM.createPortal(
                    <HeaderPanelSettingsPopoverStyled style={{ top: popoverPos.top, left: popoverPos.left }}>
                        <HeaderPanelSettings code={code!} panels={panels} onClose={onSettingsClose} isMobile={false} />
                    </HeaderPanelSettingsPopoverStyled>,
                    document.body
                )
            }
        </div>
    )
}
